import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";

import { updateProfile } from "@/api/auth";
import { useAuth } from "@/auth/useAuth";
import { Alert } from "@/components/Alert";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { Toast } from "@/components/Toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  applyAppearance,
  DARK_THEME_OPTIONS,
  DEFAULT_UI_FONT,
  DEFAULT_UI_THEME,
  LIGHT_THEME_OPTIONS,
  UI_FONT_OPTIONS,
} from "@/lib/appearance";

function ThemeSwatchGrid({ options, theme, onSelect, groupLabel }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">{groupLabel}</h3>
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5"
        role="radiogroup"
        aria-label={groupLabel}
      >
        {options.map((opt) => {
          const selected = theme === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(opt.value)}
              className={cn(
                "flex flex-col items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                selected
                  ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                  : "border-border hover:border-border-hover hover:bg-muted/40"
              )}
            >
              <span className="relative flex h-8 w-full overflow-hidden rounded-md border border-black/5">
                {opt.colors.map((hex, i) => (
                  <span
                    key={`${opt.value}-${i}`}
                    className="h-full flex-1"
                    style={{ backgroundColor: hex }}
                  />
                ))}
                {selected ? (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Check className="size-4 text-white drop-shadow" aria-hidden />
                  </span>
                ) : null}
              </span>
              <span className="text-sm font-medium text-foreground">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AppearancePage() {
  const { user, setUser } = useAuth();

  const savedTheme = user?.ui_theme || DEFAULT_UI_THEME;
  const savedFont = user?.ui_font || DEFAULT_UI_FONT;

  const [theme, setTheme] = useState(savedTheme);
  const [font, setFont] = useState(savedFont);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  useEffect(() => {
    setTheme(savedTheme);
    setFont(savedFont);
  }, [savedTheme, savedFont]);

  useEffect(() => {
    applyAppearance(theme, font);
  }, [theme, font]);

  useEffect(() => {
    return () => {
      applyAppearance(savedTheme, savedFont);
    };
  }, [savedTheme, savedFont]);

  const isDirty = useMemo(
    () => theme !== savedTheme || font !== savedFont,
    [theme, font, savedTheme, savedFont]
  );

  const discard = () => {
    setTheme(savedTheme);
    setFont(savedFont);
    applyAppearance(savedTheme, savedFont);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isDirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateProfile({ ui_theme: theme, ui_font: font });
      setUser(updated);
      setToastMessage("Appearance saved.");
    } catch (err) {
      setError(err.message || "Failed to save appearance.");
      applyAppearance(savedTheme, savedFont);
      setTheme(savedTheme);
      setFont(savedFont);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell>
      <Toast message={toastMessage} onClose={() => setToastMessage(null)} />

      <PageHeader
        title="Appearance"
        description="Customize the color theme and font for your admin panel. These choices apply only to you."
      />

      <PageBody>
        {error ? <Alert type="error">{error}</Alert> : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Color theme</CardTitle>
              <CardDescription>
                Choose a light or dark theme. The selection controls the entire admin panel look.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ThemeSwatchGrid
                groupLabel="Light themes"
                options={LIGHT_THEME_OPTIONS}
                theme={theme}
                onSelect={setTheme}
              />
              <ThemeSwatchGrid
                groupLabel="Dark themes"
                options={DARK_THEME_OPTIONS}
                theme={theme}
                onSelect={setTheme}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Font</CardTitle>
              <CardDescription>
                Choose the typeface used across the admin panel.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="grid gap-2 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Font"
              >
                {UI_FONT_OPTIONS.map((opt) => {
                  const selected = font === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setFont(opt.value)}
                      className={cn(
                        "rounded-lg border px-4 py-3 text-left transition-colors",
                        selected
                          ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                          : "border-border hover:border-border-hover hover:bg-muted/40"
                      )}
                    >
                      <span
                        className="block text-base font-semibold text-foreground"
                        style={{ fontFamily: opt.family }}
                      >
                        {opt.label}
                      </span>
                      <span
                        className="mt-1 block text-sm text-muted-foreground"
                        style={{ fontFamily: opt.family }}
                      >
                        The quick brown fox jumps over the lazy dog.
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
            <CardFooter className="gap-2 border-t border-border px-4 pt-4">
              <Button type="submit" disabled={!isDirty || saving}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={!isDirty || saving}
                onClick={discard}
              >
                Discard
              </Button>
            </CardFooter>
          </Card>
        </form>
      </PageBody>
    </PageShell>
  );
}
